import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Phone, MessageCircle, ChevronDown, ArrowRight, ArrowUpRight, Instagram, Facebook, Youtube, Star } from "lucide-react";
import { Reveal, Eyebrow, SectionHead, CTAButton, Marquee } from "@/components/bits";
import Logo from "@/components/Logo";
import { BRAND, IMAGES, ASSETS, ACTIVITIES, DIFFERENTIATORS, BRANCHES, PORTFOLIO_ITEMS, GOOGLE_REVIEWS, waLink } from "@/data/site";
import * as Icons from "lucide-react";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero">
      <motion.div
        className="absolute inset-0 scale-110 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.hero})`, y: reduce ? 0 : bgY }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-[#050507]/72 to-black/45" aria-hidden="true" />
      <div className="hero-vignette absolute inset-0" aria-hidden="true" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(76,5,25,0.5),transparent_55%)]" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-[1440px] px-5 pb-32 pt-40 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-red-600" aria-hidden="true" />
          <span className="font-mono2 text-[11px] uppercase tracking-[0.34em] text-red-500">
            New Boogie Woogie Academy · 30+ Years
          </span>
        </motion.div>

        <h1 className="max-w-5xl font-display text-[10vw] font-black uppercase leading-[0.98] tracking-tight sm:text-7xl lg:text-[86px]">
          <MaskedLine delay={0.3}>Where talent</MaskedLine>
          <MaskedLine delay={0.42}>
            finds <span className="text-red-500">direction.</span>
          </MaskedLine>
          <MaskedLine delay={0.62}>Where passion becomes</MaskedLine>
          <MaskedLine delay={0.74}>
            <span className="text-outline-red">performance.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-8 max-w-xl text-sm leading-relaxed text-zinc-300 sm:text-base"
        >
          30+ years of experience in training, creativity &amp; performance — a multi-activity academy where
          discipline builds confidence, and confidence commands the stage.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <CTAButton href={BRAND.phoneLink} testid="hero-call-btn">
            <Phone size={15} /> Call Now
          </CTAButton>
          <CTAButton href={waLink()} variant="whatsapp" testid="hero-whatsapp-btn">
            <MessageCircle size={15} /> WhatsApp Us
          </CTAButton>
          <CTAButton href="/booking?mode=enquiry" variant="ghost" testid="hero-enquire-btn">
            Enquire Now <ArrowRight size={15} />
          </CTAButton>
        </motion.div>

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35 }}
          onClick={() => document.getElementById("credibility")?.scrollIntoView({ behavior: "smooth" })}
          className="group mt-14 flex items-center gap-3 font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-400 transition-colors hover:text-red-400"
          data-testid="hero-explore-btn"
        >
          Explore our activities
          <ChevronDown size={15} className="animate-bounce text-red-500" />
        </motion.button>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.25, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="absolute bottom-28 right-8 hidden lg:block"
        data-testid="hero-experience-badge"
      >
        <div className="relative flex h-36 w-36 items-center justify-center">
          <svg className="spin-slow absolute inset-0 h-full w-full" viewBox="0 0 100 100" aria-hidden="true">
            <defs>
              <path id="badge-circle" d="M50,50 m-40,0 a40,40 0 1,1 80,0 a40,40 0 1,1 -80,0" />
            </defs>
            <text fontSize="8.4" letterSpacing="3.1" fill="#E2E8F0" opacity="0.85">
              <textPath href="#badge-circle">30+ YEARS OF EXPERIENCE · ESTABLISHED LEGACY ·</textPath>
            </text>
          </svg>
          <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full border border-red-600/50 bg-black/60 backdrop-blur-md">
            <span className="font-display text-2xl font-black text-red-500">30+</span>
            <span className="font-mono2 text-[8px] uppercase tracking-[0.22em] text-zinc-400">Years</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

const CredibilityBar = () => {
  const items = [
    { big: "30+", label: "Years of Experience" },
    { big: "14+", label: "Activity Categories" },
    { big: "Multi", label: "Multi-Activity Training" },
    { big: "P·F·C", label: "Performance · Fitness · Creativity" },
  ];
  return (
    <section id="credibility" className="border-y border-white/10 bg-[#0A0A0E]" data-testid="credibility-bar">
      <div className="mx-auto grid max-w-[1440px] grid-cols-2 lg:grid-cols-4">
        {items.map((it, i) => (
          <Reveal key={it.label} delay={i * 0.08} className={`border-white/10 px-6 py-10 sm:px-10 ${i !== 0 ? "border-l max-lg:border-l-0 max-lg:odd:border-l-0" : ""} ${i % 2 === 1 ? "max-lg:border-l" : ""} ${i > 1 ? "max-lg:border-t" : ""}`}>
            <p className="font-display text-4xl font-black tracking-tight text-red-500 sm:text-5xl">{it.big}</p>
            <p className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.22em] text-zinc-400">{it.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const StoryStrip = () => {
  const steps = ["30+ Years of Experience", "Discipline", "Training", "Creativity", "Confidence", "Performance", "A Lifelong Journey"];
  return (
    <section className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8" data-testid="brand-story">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-4">
        {steps.map((s, i) => (
          <Reveal key={s} delay={i * 0.06} className="flex items-center gap-4">
            <span className={`font-display text-sm font-extrabold uppercase tracking-[0.14em] sm:text-base ${i === steps.length - 1 ? "text-red-500" : "text-white"}`}>
              {s}
            </span>
            {i < steps.length - 1 && <ArrowRight size={15} className="text-red-600" aria-hidden="true" />}
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const AboutTeaser = () => (
  <section className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 sm:py-28" data-testid="about-teaser">
    <div className="grid items-center gap-14 lg:grid-cols-2">
      <Reveal className="img-zoom relative overflow-hidden">
        <img src={ASSETS.studio2} alt="Inside the New Boogie Woogie Academy studio — mirrored training hall" className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050507]/70 to-transparent" aria-hidden="true" />
        <div className="absolute bottom-6 left-6 border-l-2 border-red-600 pl-4">
          <p className="font-display text-5xl font-black text-white">30+</p>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-zinc-300">Years of legacy</p>
        </div>
      </Reveal>
      <div>
        <SectionHead
          eyebrow="About the Academy"
          title={<>A vibrant space where passion, discipline &amp; creativity come together</>}
        />
        <Reveal delay={0.15}>
          <p className="mt-6 text-sm leading-relaxed text-zinc-400 sm:text-base">
            New Boogie Woogie Academy is a vibrant and dedicated learning space committed to helping students
            discover their potential through quality training, structured classes, professional guidance, and a
            positive learning environment.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-zinc-400 sm:text-base">
            Whether a student is taking their first steps or developing existing skills, we provide an environment
            where they can learn with confidence and grow at their own pace.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["Discipline", "Confidence", "Coordination", "Creativity", "Teamwork", "Stage Presence", "Self-Expression"].map((v) => (
              <span key={v} className="border border-white/12 px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:border-red-500/60 hover:text-red-400">
                {v}
              </span>
            ))}
          </div>
          <CTAButton href="/about" variant="ghost" className="mt-10" testid="about-teaser-btn">
            Our Story <ArrowUpRight size={15} />
          </CTAButton>
        </Reveal>
      </div>
    </div>
  </section>
);

const Philosophy = () => {
  const lines = ["Learn with discipline.", "Practice with dedication.", "Perform with confidence.", "Grow with passion."];
  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-[#08080B] py-24 sm:py-32" data-testid="philosophy">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,rgba(76,5,25,0.35),transparent_60%)]" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8">
        <Eyebrow>Our Philosophy</Eyebrow>
        <div className="mt-8 space-y-2">
          {lines.map((l, i) => (
            <Reveal key={l} delay={i * 0.1}>
              <p className={`font-display text-3xl font-black uppercase leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl ${i % 2 === 1 ? "text-outline pl-6 sm:pl-16" : "text-white"}`}>
                {l}
              </p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.45}>
          <p className="mt-12 max-w-xl border-l-2 border-red-600 pl-5 text-sm leading-relaxed text-zinc-400 sm:text-base">
            New Boogie Woogie Academy — where talent finds direction and passion becomes performance.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const Differentiators = () => (
  <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 sm:py-32" data-testid="differentiators">
    <SectionHead
      eyebrow="Why Choose Us"
      title={<>What makes New Boogie Woogie Academy <span className="text-red-500">different</span></>}
    />
    <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {DIFFERENTIATORS.map((d, i) => {
        const Icon = Icons[d.icon] || Icons.Sparkles;
        return (
          <Reveal key={d.title} delay={(i % 4) * 0.07}>
            <article className="card-glow group h-full border border-white/10 bg-[#0F0F14] p-7" data-testid={`differentiator-${i}`}>
              <span className="font-mono2 text-[10px] text-red-600">0{i + 1}</span>
              <Icon size={26} className="mt-4 text-red-500 transition-transform duration-500 group-hover:scale-110" aria-hidden="true" />
              <h3 className="mt-5 font-display text-base font-extrabold uppercase tracking-wide text-white">{d.title}</h3>
              <p className="mt-3 text-[13px] leading-relaxed text-zinc-400">{d.desc}</p>
            </article>
          </Reveal>
        );
      })}
    </div>
    <Reveal delay={0.2}>
      <p className="mx-auto mt-14 max-w-3xl text-center font-display text-lg font-bold uppercase tracking-wide text-zinc-300 sm:text-xl">
        We don't just teach activities — <span className="text-red-500">we create an environment</span> where
        discipline builds consistency, guidance builds confidence, and passion turns into performance.
      </p>
    </Reveal>
  </section>
);

const ActivitiesOverview = () => {
  const cats = [
    { name: "Dance", img: IMAGES.bollywood, note: "Western · Hip-Hop · Contemporary · Semi-Classical · Kathak · Bollywood" },
    { name: "Fitness & Wellness", img: IMAGES.zumba, note: "Zumba · Aerobics · Yoga" },
    { name: "Music & Creative Arts", img: IMAGES.guitar, note: "Guitar · Singing · Painting" },
    { name: "Performance & Personal Development", img: IMAGES.acting, note: "Gymnastics · Karate · Modelling · Acting" },
  ];
  return (
    <section className="border-y border-white/10 bg-[#08080B] py-24 sm:py-32" data-testid="activities-overview">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead eyebrow="What We Teach" title={<>14+ activities. <span className="text-red-500">One academy.</span></>} />
          <Reveal delay={0.1}>
            <CTAButton href="/activities" variant="ghost" testid="activities-overview-btn">
              All Activities <ArrowUpRight size={15} />
            </CTAButton>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cats.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.08}>
              <Link
                to={`/activities?cat=${encodeURIComponent(c.name)}`}
                className="img-zoom group relative block overflow-hidden border border-white/10"
                data-testid={`activity-cat-${i}`}
              >
                <img src={c.img} alt={`${c.name} classes at New Boogie Woogie Academy, Patna`} className="aspect-[3/4] w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" aria-hidden="true" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <span className="font-mono2 text-[10px] tracking-[0.3em] text-red-500">0{i + 1}</span>
                  <h3 className="mt-2 font-display text-lg font-black uppercase leading-tight tracking-tight text-white">{c.name}</h3>
                  <p className="mt-2 text-[11px] leading-relaxed text-zinc-400">{c.note}</p>
                  <span className="mt-4 inline-flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.22em] text-red-400 opacity-0 transition-all duration-500 group-hover:opacity-100">
                    Explore <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const EventsTeaser = () => {
  const panels = [
    { title: "Riti Riwaz Event Planner", sub: "Turning special moments into unforgettable performances — weddings, sangeet, mehendi, haldi & more.", img: ASSETS.ritiRiwaz, contain: true },
    { title: "Birthday Party Entertainment", sub: "Rock band, singing, anchoring, dance performances, decoration & a grand birthday entry.", img: IMAGES.birthday },
    { title: "Mrs. Beauty Mom", sub: "A special platform exclusively for mothers — ramp show & beauty contest.", img: ASSETS.mrsBeautyMom, contain: true },
  ];
  return (
    <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 sm:py-32" data-testid="events-teaser">
      <SectionHead eyebrow="Events & Entertainment" title={<>Beyond the studio, <span className="text-red-500">onto every stage</span></>} />
      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
        {panels.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1}>
            <Link to="/events" className="img-zoom group relative block overflow-hidden border border-white/10 bg-black" data-testid={`event-panel-${i}`}>
              <img
                src={p.img}
                alt={p.title}
                className={`aspect-[4/5] w-full ${p.contain ? "object-contain p-8 sm:p-10" : "object-cover"}`}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" aria-hidden="true" />
              <div className="absolute inset-x-0 bottom-0 p-7">
                <h3 className="font-display text-xl font-black uppercase tracking-tight text-white">{p.title}</h3>
                <p className="mt-3 text-[12px] leading-relaxed text-zinc-400">{p.sub}</p>
                <span className="mt-5 inline-flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.22em] text-red-400">
                  Discover <ArrowRight size={12} className="transition-transform duration-500 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const AgeRange = () => (
  <section className="relative overflow-hidden border-y border-white/10 bg-[#0A0A0E] py-24 sm:py-28" data-testid="age-range">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_30%,rgba(220,38,38,0.12),transparent_55%)]" aria-hidden="true" />
    <div className="relative mx-auto max-w-[1440px] px-5 text-center sm:px-8">
      <Reveal>
        <Eyebrow>There's a place for your passion</Eyebrow>
        <p className="mt-8 font-display text-5xl font-black uppercase tracking-tight sm:text-7xl">
          4<span className="text-red-500">+</span> to 40 <span className="text-outline">years</span>
        </p>
        <p className="mt-4 font-mono2 text-[11px] uppercase tracking-[0.32em] text-zinc-400">For all genders · All levels</p>
        <p className="mx-auto mt-8 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
          The academy welcomes learners across different age groups and interests — from a child's very first
          class to adults rediscovering their passion for movement, music and performance.
        </p>
        <CTAButton href="/booking" className="mt-10" testid="age-range-book-btn">
          Book an Appointment
        </CTAButton>
      </Reveal>
    </div>
  </section>
);

const Branches = () => (
  <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 sm:py-32" data-testid="branches">
    <SectionHead eyebrow="Find Us" title={<>Two branches. <span className="text-red-500">One legacy.</span></>} />
    <div className="mt-14 grid gap-5 md:grid-cols-2">
      {BRANCHES.map((c, i) => (
        <Reveal key={c.id} delay={i * 0.1}>
          <article className="card-glow relative overflow-hidden border border-white/10 bg-[#0F0F14] p-8 sm:p-10" data-testid={`branch-card-${c.id}`}>
            <span className="font-mono2 text-[10px] tracking-[0.3em] text-red-600">{c.label}</span>
            <h3 className="mt-3 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{c.name}</h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-400">{c.address}</p>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-zinc-500">
              Open {BRAND.hours}. Call ahead or get turn-by-turn directions on Google Maps.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CTAButton href={c.maps} testid={`branch-directions-${c.id}`}>
                <Icons.MapPin size={15} /> Get Directions
              </CTAButton>
              <CTAButton href={BRAND.phoneLink} variant="ghost" testid={`branch-call-${c.id}`}>
                <Phone size={15} /> Call Now
              </CTAButton>
            </div>
          </article>
        </Reveal>
      ))}
    </div>
  </section>
);

const ReviewsPreview = () => (
  <section className="border-y border-white/10 bg-[#08080B] py-24 sm:py-32" data-testid="reviews-preview">
    <div className="mx-auto max-w-[1440px] px-5 text-center sm:px-8">
      <Reveal>
        <Eyebrow>What our students &amp; families say</Eyebrow>
        <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">
          Genuine words, straight from <span className="text-red-500">Google</span>
        </h2>
        <figure className="mx-auto mt-8 max-w-2xl border border-white/10 bg-[#0F0F14] p-8 text-left">
          <blockquote className="text-sm leading-relaxed text-zinc-300">“{GOOGLE_REVIEWS[0].text}”</blockquote>
          <figcaption className="mt-5 flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-black text-[#4285F4]" aria-hidden="true">G</span>
            <span className="font-mono2 text-[9px] uppercase tracking-[0.22em] text-zinc-500">{GOOGLE_REVIEWS[0].name} · Verified Google Review</span>
          </figcaption>
        </figure>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <CTAButton href={BRANCHES[0].maps} testid="reviews-google-btn">
            <Star size={15} /> View All Google Reviews
          </CTAButton>
          <CTAButton href="/reviews" variant="ghost" testid="reviews-page-btn">
            Reviews Page <ArrowUpRight size={15} />
          </CTAButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const PortfolioPreview = () => (
  <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 sm:py-32" data-testid="portfolio-preview">
    <div className="flex flex-wrap items-end justify-between gap-6">
      <SectionHead eyebrow="Portfolio" title={<>Moments that <span className="text-red-500">own the stage</span></>} />
      <Reveal delay={0.1}>
        <CTAButton href="/portfolio" variant="ghost" testid="portfolio-preview-btn">
          Full Portfolio <ArrowUpRight size={15} />
        </CTAButton>
      </Reveal>
    </div>
    <div className="mt-14 grid grid-cols-2 gap-4 lg:grid-cols-4">
      {PORTFOLIO_ITEMS.slice(0, 4).map((p, i) => (
        <Reveal key={p.img} delay={i * 0.08}>
          <Link to="/portfolio" className="img-zoom group relative block overflow-hidden border border-white/10">
            <img src={p.img} alt={p.caption} className={`w-full object-cover ${i % 2 === 0 ? "aspect-[3/4]" : "aspect-[3/4] lg:mt-12"}`} loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
            <p className="absolute bottom-4 left-4 font-mono2 text-[10px] uppercase tracking-[0.22em] text-red-400 opacity-0 transition-opacity duration-500 group-hover:opacity-100">{p.cat}</p>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Social = () => {
  const cards = [
    { icon: Instagram, name: "@anilraj0072", href: BRAND.instagram1, label: "Instagram" },
    { icon: Instagram, name: "@newboogiewoogieacademy1", href: BRAND.instagram2, label: "Instagram" },
    { icon: Facebook, name: "Facebook", href: BRAND.facebook, label: "Follow on Facebook" },
    { icon: Youtube, name: "YouTube", href: BRAND.youtube, label: "Subscribe on YouTube" },
  ];
  return (
    <section className="border-t border-white/10 bg-[#08080B] py-24 sm:py-28" data-testid="social">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <SectionHead eyebrow="Social" title="Follow the journey" align="center" />
        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2">
          {cards.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.07}>
              <a
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                className="card-glow group flex items-center justify-between border border-white/10 bg-[#0F0F14] px-6 py-5"
                data-testid={`social-card-${i}`}
              >
                <span className="flex items-center gap-4">
                  <c.icon size={20} className="text-red-500" aria-hidden="true" />
                  <span>
                    <span className="block text-sm font-semibold text-white">{c.name}</span>
                    <span className="block font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">{c.label}</span>
                  </span>
                </span>
                <ArrowUpRight size={16} className="text-zinc-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-red-400" />
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section className="relative overflow-hidden py-28 sm:py-36" data-testid="final-cta">
    <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${IMAGES.stage})` }} aria-hidden="true" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#050507] via-transparent to-[#050507]" aria-hidden="true" />
    <div className="relative mx-auto max-w-[1440px] px-5 text-center sm:px-8">
      <Reveal>
        <Logo />
        <h2 className="mx-auto mt-8 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-6xl">
          Your stage is <span className="text-red-500">waiting.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-zinc-400 sm:text-base">
          Join a legacy built over three decades. Take the first step — our team will guide you the rest of the way.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <CTAButton href="/booking" testid="final-book-btn">Book an Appointment</CTAButton>
          <CTAButton href={waLink()} variant="whatsapp" testid="final-whatsapp-btn">
            <MessageCircle size={15} /> WhatsApp Us
          </CTAButton>
          <CTAButton href={BRAND.phoneLink} variant="ghost" testid="final-call-btn">
            <Phone size={15} /> Call Now
          </CTAButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <CredibilityBar />
    <StoryStrip />
    <AboutTeaser />
    <Marquee items={["Discipline", "Confidence", "Creativity", "Performance", "Dedication", "Stage Presence", "Self-Expression"]} />
    <Philosophy />
    <Differentiators />
    <ActivitiesOverview />
    <EventsTeaser />
    <AgeRange />
    <Branches />
    <ReviewsPreview />
    <PortfolioPreview />
    <Social />
    <FinalCTA />
  </>
);

export default Home;
